import * as THREE from 'three';
import React from 'react';
import Engine from "./Engine";
import MaterialAsset from "./MaterialAsset";
import Scenario from "./scenarioElements/Scenario";
import { assert } from "./utilities/ErrorHandling";
import { getUuidv4 } from './utilities/Misc';
import ReplaceAudioAsset from "../actions/assetSwap/ReplaceAudioAsset";
import ReplaceImageAsset from "../actions/assetSwap/ReplaceImageAsset";
import ReplaceMaterial from "../actions/assetSwap/ReplaceMaterial";
import ReplaceUITemplate from "../actions/assetSwap/ReplaceUITemplate";
import ReplaceVideoAsset from "../actions/assetSwap/ReplaceVideoAsset";
import AssetsLibraryUtilities from "../components/filebrowser/AssetsLibraryUtilities";
import ModalManager from "../components/layout/ModalManager";
import StringProperty from "../components/properties/StringProperty";
import { UIPropertyTypes } from "../components/uisystem/UIEditorComponentRenderer";
import { AssetTypes, DndItemTypes, FileIOType } from "../globals";
import { IAssetInfo, IAssetProps, IProjectAssetInfo } from "../interfaces/Assets";
import store, { StoreActions } from "../state/store";
import Dialog from "../components/layout/Dialog";
import AssetSwapDialog from '../components/filebrowser/AssetSwapDialog';
import ReplaceUITemplateAction from '../actions/visageUI/ReplaceUITemplateAction';
import { AssetSubTrees, IAssetLibrary } from '../interfaces/AssetLibrary';
import { getAssetsFromGrapesJS, traverseGrapesProject } from '../components/visageUI/GrapesJSUtilities';
import { IGrapesJSProjectData } from '../interfaces/VisageUI';
import { GjsAttributeNames } from '../components/visageUI/gjsplugin/Enums';

export type AssetsInProjectOutput = {
	audioAssets: Set<string>;
	materialAssets: Set<string>;
	modelAssets: Set<string>;
	videoAssets: Set<string>;
	imageAssets: Set<string>;
	uiTemplateAssets: Set<string>;
	visageTemplateAssets: Set<string>;
};

export interface FileLoadProgressDetails {
	id: string;
	url?: string;
	progress: number;
	name?: string;
	cancel?: () => void;
	type?: FileIOType;
	request?: XMLHttpRequest;
}

const fileDownloadsMap = new Map();
const fileUploadsMap = new Map<string, File>();
const fileLoadProgressMap: Map<string, FileLoadProgressDetails> = new Map();
const assetPropsMap = new Map<string, IAssetProps>(); // TODO: move to asset library window? if not, we must generate this regardless of asset library having rendered!
let usedAssets: {
	[assetId: string]: number;
} = {};

export default class AssetsManager
{
	static clearUsedAssets()
	{
		usedAssets = {};
	}

	static addUsedAsset(assetId: string, count = 1)
	{
		//console.log("adding", assetId, count);

		if(!usedAssets[assetId])
		{
			usedAssets[assetId] = count;
		}
		else
		{
			usedAssets[assetId] += count;
		}

		//console.log({...usedAssets});
		AssetsLibraryUtilities.refresh();
	}

	static removeUsedAsset(assetId: string, count = 1)
	{
		//console.log("removing", assetId, count);

		if(usedAssets[assetId])
		{
			usedAssets[assetId] -= count;
			assert(usedAssets[assetId] >= 0, "asset count should never be less than 0");
		}

		//console.log({...usedAssets});
		AssetsLibraryUtilities.refresh();
	}

	static getAssetUseCount(assetId: string)
	{
		return usedAssets[assetId];
	}

	static isAssetUsedInProject(assetId: string)
	{
		// asset is not used if we don't have it registered, or if its use count is 0
		// some assets such as materials or visage templates are not directly registered in usedAssets
		if(usedAssets[assetId] !== undefined && usedAssets[assetId] > 0) 
		{
			return true;
		}

		// check if it's a visage template
		if(Object.values<string>(store.getState().visageModule.stepTemplateMap).some(templateId => templateId === assetId)) 
		{
			return true;
		}

		// check if it's used in any materials
		for(const materialAsset of MaterialAsset.getMaterialAssets())
		{
			if(materialAsset.isAssignedToAnyObject())
			{
				// the material itself
				if(materialAsset.guid === assetId) 
				{
					return true;
				}

				// any of its maps
				for(const map of materialAsset.getMapsInUse())
				{
					if(map?.userData?.assetId === assetId) 
					{
						return true;
					}
				}
			}
		}

		// check if it's used in any visage template or project
		for(const [stepId, uiData] of Object.entries(store.getState().visageModule.stepUIData))
		{
			if(Scenario.getStep(stepId)?.isDeleted()) continue;

			for(const visageAssetId of getAssetsFromGrapesJS(uiData))
			{
				if(visageAssetId === assetId) 
				{
					return true;
				}
			}
		}
		for(const templateData of Object.values(store.getState().visageModule.uiTemplateData))
		{
			for(const visageAssetId of getAssetsFromGrapesJS(templateData.content))
			{
				if(visageAssetId === assetId) 
				{
					return true;
				}
			}
		}

		// not registered nor used in any materials or visage templates
		return false;
	}

	static setPropsForAsset(assetId: string, assetProps: any)
	{
		assetPropsMap.set(assetId, { ...assetProps });
	}

	static getAssetTag(assetId: string)
	{
		const library = store.getState().assetModule.library;
		const materials = store.getState().materialsModule.materials;
		const slideMaster = store.getState().uiModule.slideMaster;
		const visage = store.getState().visageModule.uiTemplateData;

		if(!library) return "";

		if(library.user && library.user[assetId]) return AssetSubTrees.User;
		else if (library.organization && library.organization[assetId]) return AssetSubTrees.Organization;
		else if ((materials && materials[assetId]) || (slideMaster && slideMaster[assetId])) return AssetSubTrees.Project;
		else if (visage && visage[assetId]) return AssetSubTrees.Project;
		else
		{
			for(const assetInfo of Object.values<IAssetInfo>(library.user))
			{
				if(assetInfo.revisions)
				{
					for(const revision of Object.values<IAssetInfo>(assetInfo.revisions))
					{
						if(revision.id === assetId) return AssetSubTrees.User;
					}
				}
			}

			for(const assetInfo of Object.values<IAssetInfo>(library.organization))
			{
				if(assetInfo.revisions)
				{
					for(const revision of Object.values(assetInfo.revisions))
					{
						if(revision.id === assetId) return AssetSubTrees.Organization;
					}
				}
			}
		}
	}

	static getAssetSize(assetInfo: IAssetInfo)
	{
		if (assetInfo.type === AssetTypes.Revision)
		{
			return assetInfo.processed_file_size;
		}
		else if (assetInfo.type === AssetTypes.Model)
		{
			return assetInfo.processed_size;
		}

		return assetInfo.raw_size;
	}

	/**
	 * returns a flat list of all assets in the project
	 * taking the library, materials, slide master, visage templates, and project assets
	 * and returning them as a single object
	 *
	 * @static
	 * @return {*}  {Record<string, IAssetInfo>}
	 * @memberof AssetsManager
	 */
	static getAllAssetsFlat(): Record<string, IAssetInfo>
	{
		const library: IAssetLibrary | null = store.getState().assetModule.library;
		const materials = store.getState().materialsModule.materials;
		const slideMaster = store.getState().uiModule.slideMaster;
		const visage = store.getState().visageModule.uiTemplateData;
		const project: Record<string, IProjectAssetInfo> = store.getState().assetModule.project;
		const foreign: Record<string, IAssetInfo> = store.getState().assetModule.foreign;
		return { 
			...AssetsManager.flattenLibraryAssets(library?.organization), 
			...AssetsManager.flattenLibraryAssets(library?.user), 
			...materials, 
			...slideMaster, 
			...project, 
			...visage, 
			...AssetsManager.flattenLibraryAssets(foreign)
		};
	}

	static flattenLibraryAssets(libraryAssetContainer?: Record<string, IAssetInfo>)
	{
		const output = {};

		if(!libraryAssetContainer) return output;

		for(const assetInfo of Object.values<IAssetInfo>(libraryAssetContainer))
		{
			output[assetInfo.id] = assetInfo;
			if(assetInfo.revisions)
			{
				for(const revision of assetInfo.revisions)
				{
					output[revision.id] = revision;
				}
			}
		}

		return output;
	}

	static getPropsForAsset(assetId: string): IAssetProps | undefined
	{
		if(!assetPropsMap.has(assetId))
		{
			const allAssetsFlat = AssetsManager.getAllAssetsFlat();

			// extract revisions
			for(const assetInfo of Object.values<IAssetInfo>(allAssetsFlat))
			{
				if(assetInfo.revisions)
				{
					for(const revision of Object.values(assetInfo.revisions))
					{
						allAssetsFlat[revision.id] = { ...revision };
					}
				}
			}

			if(!allAssetsFlat[assetId]) return;

			const assetInfo = AssetsManager.getAssetInfo(assetId);
			if(!assetInfo) return;

			const size = AssetsManager.getAssetSize(assetInfo) ?? NaN;
			const tag = AssetsManager.getAssetTag(assetId) ?? "";

			const nodeProps: IAssetProps =
			{
				key: assetInfo.id,
				nodeId: assetInfo.id,
				assetId: assetInfo.id,
				type: assetInfo.type,
				folder: assetInfo.folder ? assetInfo.folder : tag,
				parent: assetInfo.folder ? assetInfo.folder : tag,
				tag: tag,
				label: assetInfo.name,
				creationDate: assetInfo.creation_date ? assetInfo.creation_date : assetInfo.file_creation_date,
				isActualAsset: true,
				size: size,
				dndOverrides: {
					dragType: DndItemTypes.ASSET_TREE_ITEM,
				},
				children: [],
			};

			assetPropsMap.set(assetId, nodeProps);
		}

		return assetPropsMap.get(assetId);
	}

	/**
	 * Registers the given files as uploads
	 * @param files
	 * @returns the ids of the registered uploads
	 */
	static registerUploads(files: File[])
	{
		const uploads = new Array<string>();
		for (const file of files)
		{
			const id = getUuidv4();
			fileUploadsMap.set(id, file);
			uploads.push(id);
		}

		store.dispatch(StoreActions.files.registerUploads(uploads));

		return uploads;
	}

	static clearUploads()
	{
		fileUploadsMap.clear();

		store.dispatch(StoreActions.files.clearUploads());
	}

	static registerDownloads(files: any[])
	{
		const downloads = new Array<string>();
		for (const file of files)
		{
			fileDownloadsMap.set(file.id, file);
			downloads.push(file.id);
		}

		store.dispatch(StoreActions.files.registerDownloads(downloads));
	}

	static clearDownloads()
	{
		fileDownloadsMap.clear();

		store.dispatch(StoreActions.files.clearDownloads());
	}

	static getUploadFile(fileId: string)
	{
		return fileUploadsMap.get(fileId);
	}

	static getDownloadFile(fileId: string)
	{
		return fileDownloadsMap.get(fileId);
	}

	static registerFileLoadProgress(details: FileLoadProgressDetails)
	{
		fileLoadProgressMap.set(details.id, { ...details });

		store.dispatch(StoreActions.files.registerFileLoadProgress({ fileId: details.id }));
	}

	static getFileLoadProgress(fileId: string)
	{
		return fileLoadProgressMap.get(fileId);
	}

	static removeFileLoadProgress(fileId: string)
	{
		fileLoadProgressMap.delete(fileId);
		store.dispatch(StoreActions.files.removeFileLoad({ fileId: fileId }));
	}

	static getFileProgressMap()
	{
		return fileLoadProgressMap;
	}

	static rebuildUsedAssets()
	{
		AssetsManager.clearUsedAssets();
		AssetsLibraryUtilities.refresh();
	}

	static getAssetsInProject()
	{
		// usually would happen on the first time a project is opened, when usedAssets is empty we run manual scan and populate it
		if(!Object.keys(usedAssets).length)
		{
			AssetsManager.scanAssetsInProject();
		}

		const output: AssetsInProjectOutput = {
			audioAssets: new Set<string>(),
			materialAssets: new Set<string>(),
			modelAssets: new Set<string>(),
			videoAssets: new Set<string>(),
			imageAssets: new Set<string>(),
			uiTemplateAssets: new Set<string>(),
			visageTemplateAssets: new Set<string>(),
		};

		for(const [key, value] of Object.entries<number>(usedAssets))
		{
			if(value > 0)
			{
				const assetProps = AssetsManager.getPropsForAsset(key);
				if(!assetProps) continue;

				switch(assetProps.type)
				{
					case AssetTypes.Audio:
						output.audioAssets.add(key);
						break;
					case AssetTypes.Video:
						output.videoAssets.add(key);
						break;
					case AssetTypes.Model:
					case AssetTypes.ModelProcessed:
					case AssetTypes.Revision:
					case AssetTypes.RevisionProcessed:
						output.modelAssets.add(key);
						break;
					case AssetTypes.Image:
						output.imageAssets.add(key);
						break;
					case AssetTypes.Material:
						//output.materialAssets.add(key);
						// materials are tracked differently (see below)
						break;
					case AssetTypes.UITemplate:
						output.uiTemplateAssets.add(key);
						break;
					case AssetTypes.VisageTemplate:
						//output.visageTemplateAssets.add(key);
						// visage templates are tracked differently (see below)
						break;
				}
			}
		}

		// materials
		for(const materialAsset of MaterialAsset.getMaterialAssets())
		{
			if(materialAsset.isAssignedToAnyObject())
			{
				output.materialAssets.add(materialAsset.guid);

				for(const map of materialAsset.getMapsInUse())
				{
					if(map?.userData?.assetId === undefined) continue;
					const mapProps = AssetsManager.getPropsForAsset(map.userData.assetId);
					if(!mapProps) continue;

					switch(mapProps.type)
					{
						case AssetTypes.Video:
							output.videoAssets.add(mapProps.assetId);
							break;
						case AssetTypes.Image:
							output.imageAssets.add(mapProps.assetId);
							break;
						default:
							break;
					}
				}
			}
		}

		// visage templates
		Object.values<string>(store.getState().visageModule.stepTemplateMap).forEach(templateId => output.visageTemplateAssets.add(templateId));

		// visage assets
		const visageAssetIds = new Set<string>();
		for(const [stepId, uiData] of Object.entries(store.getState().visageModule.stepUIData))
		{
			if(Scenario.getStep(stepId)?.isDeleted()) continue;

			getAssetsFromGrapesJS(uiData).forEach(assetId => visageAssetIds.add(assetId));
		}
		for(const templateData of Object.values(store.getState().visageModule.uiTemplateData))
		{
			getAssetsFromGrapesJS(templateData.content).forEach(assetId => visageAssetIds.add(assetId));
		}

		for(const assetId of visageAssetIds)
		{
			const assetProps = AssetsManager.getPropsForAsset(assetId);
			if(!assetProps) continue;

			switch(assetProps.type)
			{
				case AssetTypes.Audio:
					output.audioAssets.add(assetId);
					break;
				case AssetTypes.Video:
					output.videoAssets.add(assetId);
					break;
				case AssetTypes.Image:
					output.imageAssets.add(assetId);
					break;
				default:
					break;
			}
		}

		return output;
	}

	static scanAssetsInProject()
	{
		AssetsManager.clearUsedAssets();

		if(!Scenario.current())
			return;

		// traverse all objects in all scenes to find used assets
		for (const scene of Scenario.getAllScenes())
		{
			const sceneRootObject = Engine.getInstance().getSelectionRoot(scene.userData.guid);
			if(!sceneRootObject) 
			{
				continue;
			}

			for(const child of sceneRootObject.children)
			{
				AssetsManager.addAssetsUsedBySceneObject(child);
			}
		}

		// traverse all scenario steps to find which UI templates are in use
		const allScenarioSteps = Scenario?.getSteps();
		for (const step of allScenarioSteps)
		{
			const associatedUI = store.getState().uiModule.ui[step.id];
			if (associatedUI)
			{
				AssetsManager.addUsedAsset(associatedUI.slide);
			}
		}

		// traverse all ui data and search their content for assets
		const allUiData = store.getState().uiModule.ui;
		for (const uiData of Object.values<any>(allUiData))
		{
			for (const templateData of Object.values<any>(uiData))
			{
				for (const templatePropertyGroupData of Object.values<any>(templateData))
				{
					for (const templatePropertyData of Object.values<any>(templatePropertyGroupData))
					{
						if (templatePropertyData.type && templatePropertyData.value)
						{
							switch (templatePropertyData.type)
							{
								case UIPropertyTypes.Image:
								case UIPropertyTypes.Video:
								case UIPropertyTypes.Audio:
									AssetsManager.addUsedAsset(templatePropertyData.value);
									break;
							}
						}
					}
				}
			}
		}

		// traverse all visage ui data and search their content for assets
		for(const uiData of Object.values(store.getState().visageModule.stepUIData))
		{
			this.addAssetsUsedByGrapesProject(uiData);
		}

		// templates - need to replace both current and previous states
		for(const templateData of Object.values(store.getState().visageModule.uiTemplateData))
		{
			this.addAssetsUsedByGrapesProject(templateData.content);

			if(templateData.previousState)
			{
				this.addAssetsUsedByGrapesProject(templateData.previousState);
			}
		}
	}

	static addAssetsUsedByGrapesProject(grapesProject: IGrapesJSProjectData)
	{
		traverseGrapesProject(grapesProject, ({ component }) =>
		{
			if (component.attributes?.[GjsAttributeNames.AssetID])
			{
				AssetsManager.addUsedAsset(component.attributes[GjsAttributeNames.AssetID]);
			}

			if (component.attributes?.[GjsAttributeNames.CoverAssetID])
			{
				AssetsManager.addUsedAsset(component.attributes[GjsAttributeNames.CoverAssetID]);
			}
		});
	}

	static addAssetsUsedBySceneObject(sceneObject: THREE.Object3D)
	{
		for(const [key, value] of Object.entries<any>(AssetsManager.getAssetsUsedByObjectTree(sceneObject)))
		{
			AssetsManager.addUsedAsset(key, value);
		}
	}

	static removeAssetsUsedBySceneObject(sceneObject: THREE.Object3D)
	{
		for(const [key, value] of Object.entries<any>(AssetsManager.getAssetsUsedByObjectTree(sceneObject)))
		{
			AssetsManager.removeUsedAsset(key, value);
		}
	}

	static addAssetsUsedByMaterial(materialAsset: MaterialAsset)
	{
		for(const map of materialAsset.getMapsInUse())
		{
			if(map?.userData?.assetId === undefined) continue;
			AssetsManager.addUsedAsset(map.userData.assetId);
		}
	}

	static removeAssetsUsedByMaterial(materialAsset: MaterialAsset)
	{
		for(const map of materialAsset.getMapsInUse())
		{
			if(map?.userData?.assetId === undefined) continue;

			AssetsManager.removeUsedAsset(map.userData.assetId);
		}
	}

	static getAssetsUsedByObjectTree(rootObject: THREE.Object3D)
	{
		const output: {
			[assetId: string]: number;
		} = {};

		function countAsset(assetId: string)
		{
			if(!output[assetId])
			{
				output[assetId] = 1;
			}
			else
			{
				output[assetId] += 1;
			}
		}

		rootObject.traverse((child) =>
		{
			// materials and maps (images, videos) are tracked differently

			// audio
			if (child.userData.audio && child.userData.audio.clip)
			{
				countAsset(child.userData.audio.clip);
			}

			// model
			if (child.userData.assetId)
			{
				countAsset(child.userData.assetId);
			}
		});

		return output;
	}

	static DeleteUnassignedMaterials()
	{
		ModalManager.addModal({
			allowClickOut: false,
			content:
				<Dialog
					title={"Delete All Unassigned Materials"}
					actions={[
						{
							label: "Confirm",
							onClick: () => MaterialAsset.deleteUnassignedMaterials(),
						},
						{
							label: "Cancel",
						}
					]}
				>
					Are you sure you want to delete all unassigned materials from the project?
					<br />
					<p className='warningText'>This action cannot be undone.</p>
				</Dialog>
		})
	}

	static MergeIdenticalMaterials()
	{
		ModalManager.addModal({
			allowClickOut: false,
			content:
				<Dialog
					title={"Merge Identical Materials"}
					actions={[
						{
							label: "Confirm",
							onClick: () => MaterialAsset.mergeIdenticalMaterials(),
						},
						{
							label: "Cancel",
						}
					]}
				>
					Are you sure you want to merge all materials which share identical properties?
					<br />
					<p className='warningText'>This action cannot be undone.</p>
				</Dialog>
		})
	}

	static createFolderOnServer()
	{
		let tempFolderName = "";

		ModalManager.addModal({
			allowClickOut: false,
			content: <Dialog
				title="Create Folder"
				actions={[
					{
						label: "Confirm",
						onClick: () => AssetsLibraryUtilities.createFolderOnServer(tempFolderName !== "" ? tempFolderName : "New Folder"),
					},
					{
						label: "Cancel",
					}
				]}
			>
				<StringProperty
					label={"Enter folder name"}
					placeholder={"New Folder"}
					onChangeCallback={(v: string) => { tempFolderName = v; }}
					autoFocus={true}
				/>
				The folder will be created under the 'User' branch.
				<br />
				You may drag it to your desired location afterwards.
			</Dialog>,
		});
	}

	static replaceAssetReferences(oldAssetId: string, newAssetId: string)
	{
		const oldAssetInfo = AssetsManager.getAssetInfo(oldAssetId);
		switch (oldAssetInfo.type)
		{
			case AssetTypes.Model:
			case AssetTypes.ModelProcessed:
			case AssetTypes.Revision:
			case AssetTypes.RevisionProcessed:
				// model swap should only be handled by the dedicated UI
				ModalManager.addModal({ showBackdrop: true, content: <AssetSwapDialog sourceAssetProps={oldAssetId} /> });
				break;
			case AssetTypes.Image:
				new ReplaceImageAsset(oldAssetId, newAssetId);
				break;
			case AssetTypes.Video:
				new ReplaceVideoAsset(oldAssetId, newAssetId);
				break;
			case AssetTypes.Audio:
				new ReplaceAudioAsset(oldAssetId, newAssetId);
				break;
			case AssetTypes.UITemplate:
				new ReplaceUITemplate(oldAssetId, newAssetId);
				break;
			case AssetTypes.Material:
				new ReplaceMaterial(oldAssetId, newAssetId);
				break;
			case AssetTypes.VisageTemplate:
				new ReplaceUITemplateAction(oldAssetId, newAssetId);
				break;
		}
	}

	static getAssetInfo(assetId: string)
	{
		if(!assetId) return;
		
		const library = store.getState().assetModule.library;
		const materials = store.getState().materialsModule.materials;
		const slideMaster = store.getState().uiModule.slideMaster;
		const visage = store.getState().visageModule.uiTemplateData;
		const project: Record<string, IProjectAssetInfo> = store.getState().assetModule.project;
		const foreign: Record<string, IAssetInfo> = store.getState().assetModule.foreign;
		const allAssets = { ...library,  project: { ...project }, foreign: { ...foreign }, generated: { materials: { ...materials }, uiTemplates: { ...slideMaster }, visageTemplates: { ...visage } } };

		let output;

		if (allAssets.user && allAssets.user[assetId])
		{
			output = { ...allAssets.user[assetId] };
		}
		else if (allAssets.organization && allAssets.organization[assetId])
		{
			output = { ...allAssets.organization[assetId] };
		}
		else if(allAssets.project && allAssets.project[assetId])
		{
			output = { ...allAssets.project[assetId] };
			output.shared = true;
		}
		else if (allAssets.foreign && allAssets.foreign[assetId])
		{
			output = { ...allAssets.foreign[assetId] };
			output.shared = true;
		}
		else if (allAssets.generated.materials && allAssets.generated.materials[assetId])
		{
			if (!allAssets.generated.materials[assetId].hidden)
			{
				output = {
					id: assetId,
					type: AssetTypes.Material,
					name: allAssets.generated.materials[assetId].name.value,
					folder: "Materials",
				};
			}
		}
		else if (allAssets.generated.uiTemplates && allAssets.generated.uiTemplates[assetId])
		{
			output = {
				id: assetId,
				type: AssetTypes.UITemplate,
				name: allAssets.generated.uiTemplates[assetId].name,
				folder: "UI Templates",
			};
		}
		else if (allAssets.generated.visageTemplates && allAssets.generated.visageTemplates[assetId])
		{
			output = {
				id: assetId,
				type: AssetTypes.VisageTemplate,
				name: allAssets.generated.visageTemplates[assetId].name,
				folder: "Visage Templates",
				isTemporary: allAssets.generated.visageTemplates[assetId].isTemporary,
				isDefault: allAssets.generated.visageTemplates[assetId].isDefault,
			};
		}
		else
		{
			for (const [_key, value] of Object.entries<any>({ ...allAssets.user, ...allAssets.organization }))
			{
				if (value.revisions)
				{
					for (const revisionInfo of value.revisions)
					{
						if (revisionInfo.id === assetId)
						{
							output = { ...revisionInfo, type: AssetTypes.Revision };
							break;
						}
					}
				}

				if (output) break;
			}
		}

		return output;
	}
}
